import supabase from '../supabase/supabase'
import { uploadFile, getSignedUrl } from './storageService'

/**
 * Case Service
 * Handles all case-related database operations
 */

/**
 * Get all cases for current tenant
 * @param {Object} options - Options for filtering
 * @param {boolean} options.onlyMine - If true, only return cases created by current user (for non-admin users)
 */
export const getAllCases = async (options = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user');

    // Fetch tenant_id directly. RLS will handle security.
    const { data: member, error: memberError } = await supabase
      .from('tenant_members')
      .select('tenant_id')
      .eq('user_id', user.id)
      .single();

    if (memberError) throw memberError;
    const tenantId = member.tenant_id;

    let query = supabase
      .from('cases')
      .select(`
        *,
        staff:staff_id (
          id,
          employee_id,
          full_name,
          department,
          position
        ),
        case_stages (
          id,
          stage_name,
          stage_order,
          status,
          case_actions (
            id,
            is_completed
          )
        )
      `)
      .eq('tenant_id', tenantId)
      .is('deleted_at', null)

    // Filter by creator if onlyMine is true (for non-admin users)
    // Note: created_by stores user email, not user id
    if (options.onlyMine) {
      query = query.eq('created_by', user.email)
    }

    const { data, error } = await query.order('created_at', { ascending: false })

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching cases:', error)
    throw error
  }
}

/**
 * Get case by ID with all related data
 */
export const getCaseById = async (caseId) => {
  try {
    const { data, error } = await supabase
      .from('cases')
      .select(`
        *,
        staff:staff_id (
          id,
          employee_id,
          full_name,
          department,
          position,
          email,
          phone
        ),
        case_stages (
          id,
          stage_name,
          stage_order,
          status,
          timeline,
          started_at,
          completed_at,
          case_actions (
            id,
            action_name,
            action_order,
            is_completed,
            is_legal_critical,
            is_acas_required,
            notes,
            completed_at,
            actual_action_date
          )
        ),
        case_documents (
          id,
          document_name,
          document_type,
          file_path,
          file_size,
          description,
          uploaded_at,
          actual_document_date,
          stage_id,
          action_id
        ),
        case_notes (
          id,
          note_title,
          note_content,
          note_type,
          created_by,
          created_at
        ),
        case_audit_log (
          id,
          action,
          details,
          performed_by,
          performed_at
        )
      `)
      .eq('id', caseId)
      .is('deleted_at', null)
      .single()

    if (error) throw error

    // Get signed URLs for documents
    if (data.case_documents) {
      for (const doc of data.case_documents) {
        if (doc.file_path) {
          try {
            doc.signedUrl = await getSignedUrl('case-documents', doc.file_path, 3600)
          } catch (err) {
            console.error('Error getting signed URL:', err)
            doc.signedUrl = null
          }
        }
      }
    }

    return data
  } catch (error) {
    console.error('Error fetching case by ID:', error)
    throw error
  }
}

/**
 * Create new case by calling the secure Edge Function.
 */
export const createCase = async (caseData) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'cases',
        ...caseData,
      },
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error creating case:', error);
    throw error;
  }
}

/**
 * Update case via Edge Function
 */
export const updateCase = async (caseId, updates) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'cases/update',
        case_id: caseId,
        ...updates,
      },
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error updating case:', error)
    throw error
  }
}

/**
 * Update case action via Edge Function
 */
export const updateCaseAction = async (actionId, updates) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'case-actions/update',
        action_id: actionId,
        is_completed: updates.is_completed,
        notes: updates.notes,
        actual_action_date: updates.actual_action_date || null,  // NEW: Support backdating
      },
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error updating case action:', error)
    throw error
  }
}

/**
 * Add case note via Edge Function
 */
export const addCaseNote = async (caseId, noteData) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'case-notes',
        case_id: caseId,
        title: noteData.title,
        content: noteData.content,
        type: noteData.type || 'general',
        actual_note_date: noteData.actual_note_date || null,  // NEW: Support backdating
      },
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error adding case note:', error)
    throw error
  }
}

/**
 * Delete case note via Edge Function
 */
export const deleteCaseNote = async (noteId) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'case-notes/delete',
        note_id: noteId,
      },
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error deleting case note:', error)
    throw error
  }
}

/**
 * Upload case document (WITH FILE UPLOAD) via Edge Function
 */
export const uploadCaseDocument = async (caseId, file, documentData) => {
  try {
    // Upload file to storage first
    const uploadResult = await uploadFile('case-documents', file, {
      folder: `cases/${caseId}`
    })

    // Save metadata to database via Edge Function
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'case-documents',
        case_id: caseId,
        document_name: documentData.name || file.name,
        document_type: documentData.type,
        file_path: uploadResult.fullPath,
        file_size: uploadResult.fileSize,
        description: documentData.description,
        stage_id: documentData.stageId || null,
        action_id: documentData.actionId || null,
        actual_document_date: documentData.actual_document_date || null  // NEW: Support backdating
      },
    });

    if (error) throw error;

    return { ...data, signedUrl: uploadResult.publicUrl }
  } catch (error) {
    console.error('Error uploading case document:', error)
    throw error
  }
}

/**
 * Delete case document via Edge Function
 */
export const deleteCaseDocument = async (documentId, filePath) => {
  try {
    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('case-documents')
      .remove([filePath])

    if (storageError) console.error('Error deleting file from storage:', storageError)

    // Delete from database via Edge Function
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'case-documents/delete',
        document_id: documentId,
      },
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error deleting case document:', error)
    throw error
  }
}

/**
 * Add audit log entry
 * Note: Audit logs are now automatically created by Edge Functions
 * This function is kept for backward compatibility but may not be needed
 */
export const addCaseAuditEntry = async (caseId, action, details) => {
  console.log('Audit entry would be created:', { caseId, action, details });
  // Audit logs are now handled by Edge Functions
  return { caseId, action, details };
}

/**
 * Soft delete case via Edge Function
 */
export const deleteCase = async (caseId) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'cases/delete',
        case_id: caseId,
      },
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error deleting case:', error)
    throw error
  }
}

/**
 * Get case timeline events from view
 */
export const getCaseTimeline = async (caseId) => {
  try {
    const { data, error } = await supabase
      .from('case_timeline_view')
      .select('*')
      .eq('case_id', caseId)
      .order('event_date', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching case timeline:', error);
    throw error;
  }
};

export default {
  getAllCases,
  getCaseById,
  createCase,
  updateCase,
  updateCaseAction,
  addCaseNote,
  deleteCaseNote,
  uploadCaseDocument,
  deleteCaseDocument,
  getCaseTimeline,
  addCaseAuditEntry,
  deleteCase
}